import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import Title from 'components/Title';
import { TranslateMapContext } from 'contexts/TranslateMapContext';
import { StyledItemIcon } from 'components/SettingsBar/SettingsList/styles/SettingsListStyled';
import {
  MenuSettingsStyled,
  TitleWrapper,
  BackButtonWrapper,
  ItemsWrapper,
  StyledButton,
  CheckboxItemStyled,
  StyledText,
} from '../styled/MenuStyled';
import {
  getValuesForQualities,
  getValuesForAudioTracks,
  getValuesForSubtitles,
  renderSubmenu,
  // TODO need add after enable logic in CMS
  // renderBySubscriptionButton,
} from '../menuRenderers';
import { playerMethods } from '../../../initSkin';

export const SUBMENU_TYPE = {
  QUALITIES: 'qualities',
  AUDIO_TRACKS: 'audioTracks',
  SUBTITLES: 'subtitles',
  INTERACTIVE: 'interactive',
};

const OFF_SUBTITLES = 'off';

class MenuSettings extends PureComponent {
  static propTypes = {
    qualities: PropTypes.array,
    audioTracks: PropTypes.array,
    subtitles: PropTypes.array,
    currentQuality: PropTypes.number,
    currentAudioTrack: PropTypes.number,
    currentSubtitles: PropTypes.string,
    setQuality: PropTypes.func.isRequired,
    setAudioTrack: PropTypes.func.isRequired,
    setSubtitles: PropTypes.func.isRequired,
    autoQualityIndex: PropTypes.number,
    onBitrateBySubscriptionButtonClick: PropTypes.func.isRequired,
    onNeedToClose: PropTypes.func.isRequired,
    interactivePreferences: PropTypes.object.isRequired,
  };

  static defaultProps = {
    qualities: [],
    audioTracks: [],
    subtitles: [],
    currentQuality: null,
    currentAudioTrack: null,
    currentSubtitles: null,
    autoQualityIndex: undefined,
  };

  static contextType = TranslateMapContext;

  constructor(props) {
    super(props);
    this.state = {
      currentSubmenu: null,
      buttonsContainerHeight: null,
    };
    this.buttonsContainerRef = React.createRef();
  }

  dirtyUpdate = () => this.forceUpdate();

  componentDidMount() {
    this.setState({ // eslint-disable-line
      buttonsContainerHeight: this.buttonsContainerRef.current.offsetHeight,
    });
    this.props.interactivePreferences.subscribe(this.dirtyUpdate);
  }

  componentWillUnmount() {
    this.props.interactivePreferences.unsubscribe(this.dirtyUpdate);
  }

  componentDidUpdate(prevProps, { currentSubmenu: prevSubmenu }) {
    const { currentSubmenu } = this.state;
    if (prevSubmenu !== currentSubmenu) {
      this.setState({ // eslint-disable-line
        buttonsContainerHeight: this.buttonsContainerRef.current.offsetHeight,
      });

      if (currentSubmenu) {
        playerMethods.onSettingsCategoryOpened(currentSubmenu);
      }
    }
  }

  onBitrateBySubscriptionButtonClick = () => {
    this.props.onNeedToClose();
    this.props.onBitrateBySubscriptionButtonClick();
  };

  onClose = () => {
    if (this.state.currentSubmenu === null) {
      this.props.onNeedToClose();
    } else {
      this.setState({ currentSubmenu: null });
    }
  };

  get content() {
    const { currentSubmenu } = this.state;
    switch (currentSubmenu) {
      case SUBMENU_TYPE.QUALITIES: {
        return renderSubmenu(this.qualityOptions);
      }
      case SUBMENU_TYPE.AUDIO_TRACKS: {
        return renderSubmenu(this.audioOptions);
      }
      case SUBMENU_TYPE.SUBTITLES: {
        return renderSubmenu(this.subtitlesOptions);
      }
      case SUBMENU_TYPE.INTERACTIVE: {
        return this.interactiveOptions.map((option) => (
          <CheckboxItemStyled key={option.id}>
            <StyledText wight="600" size="fs_s" color="black" fixedSize>
              {option.label}
            </StyledText>
            <input
              type="checkbox"
              value={option.value}
              checked={option.checked}
              onChange={option.onChange}
            />
            {/* Unfortunately it seems not possible to change decorator visibility
             according to input's attribute via css selector  */}
            <StyledItemIcon
              checked={option.checked}
              name="check"
              size={20}
              theme={{ main: 'green_turquoise' }}
            />
          </CheckboxItemStyled>
        ));
      }

      default: {
        return renderSubmenu(this.firstLevelOptions);
      }
    }
  }

  get firstLevelOptions() {
    const {
      qualities,
      audioTracks,
      subtitles,
      currentQuality,
      currentAudioTrack,
      currentSubtitles,
      interactivePreferences,
    } = this.props;
    const translateContext = this.context;
    const { translateFunc } = translateContext;
    const interactiveTypes = interactivePreferences.getAll();
    const options = [];

    if (qualities.length > 1) {
      options.push({
        id: SUBMENU_TYPE.QUALITIES,
        name: translateFunc(translateContext.quality),
        value:
          currentQuality === -1
            ? translateFunc(translateContext.auto)
            : `${currentQuality}p`,
      });
    }

    if (audioTracks.length > 1) {
      options.push({
        id: SUBMENU_TYPE.AUDIO_TRACKS,
        name: translateFunc(translateContext.audiotrack),
        value: get(
          audioTracks.filter((track) => track.id === currentAudioTrack),
          [0, 'display_name']
        ),
      });
    }

    if (subtitles.filter((subs) => !subs.hidden).length > 1) {
      options.push({
        id: SUBMENU_TYPE.SUBTITLES,
        name: translateFunc(translateContext.subtitles),
        value:
          currentSubtitles === OFF_SUBTITLES
            ? translateFunc(translateContext.subtitlesOff)
            : get(
                subtitles.filter((sub) => sub.lang_tag === currentSubtitles),
                [0, 'display_name']
              ),
      });
    }

    if (interactiveTypes.length > 0) {
      const someEnabled = interactiveTypes.some((type) => type.enable);
      options.push({
        id: SUBMENU_TYPE.INTERACTIVE,
        name: translateFunc(translateContext.interactive),
        value: someEnabled
          ? translateFunc(translateContext.interactiveAllOn_full)
          : translateFunc(translateContext.interactiveAllOff_full),
      });
    }

    return options.map((option) => {
      const onTap = () => this.setState({ currentSubmenu: option.id });
      return Object.assign(option, { onTap });
    });
  }

  get qualityOptions() {
    const {
      qualities,
      currentQuality,
      autoQualityIndex,
      setQuality,
      onNeedToClose,
    } = this.props;
    const options = getValuesForQualities(
      qualities,
      currentQuality,
      autoQualityIndex
    );

    return options.map((quality) => ({
      ...quality,
      onTap() {
        setQuality(quality.id);
        onNeedToClose();
      },
    }));
  }

  get audioOptions() {
    const { audioTracks, currentAudioTrack, setAudioTrack, onNeedToClose } =
      this.props;
    const translateContext = this.context;
    const { translateFunc } = translateContext;
    const options = getValuesForAudioTracks(
      audioTracks,
      currentAudioTrack,
      translateFunc(translateContext.withSubtitles)
    );

    return options.map((track) => ({
      ...track,
      onTap() {
        setAudioTrack(track.id);
        onNeedToClose();
      },
    }));
  }

  get subtitlesOptions() {
    const { subtitles, currentSubtitles, setSubtitles, onNeedToClose } =
      this.props;
    const translateContext = this.context;
    const { translateFunc } = translateContext;
    const offText = translateFunc(translateContext.subtitlesOff);
    /*
        // TODO: надо проверить но вроде бы текущея редакция плагина вообще не будет
        //  давать недоступные сабтитлы в список, если нет и этот компонент показывает такие сабы,
        //  то этот кусок кода надо вернуть

        const currentAudioObject = audioTracks.find((a) => a.id === currentAudioTrack);
        let processingSubtitles = subtitles;
        if (currentAudioObject.require_subtitles) {
          processingSubtitles = subtitles.filter((s) => s.lang !== OFF_SUBTITLES && !s.hidden);
        }
    */
    const options = getValuesForSubtitles(subtitles, currentSubtitles, offText);

    return options.map((desc) => ({
      ...desc,
      onTap() {
        setSubtitles(desc.langTag);
        onNeedToClose();
      },
    }));
  }

  get interactiveOptions() {
    const { interactivePreferences } = this.props;
    const interactiveTypes = interactivePreferences.getAll();
    //https://jira.x2sy.com/browse/MHP-2053
    // const allOff = interactiveTypes.every((type) => !type.enable);

    return interactiveTypes.map((type) => ({
      id: type.name,
      label: type.displayName,
      value: type.name,
      checked: type.enable,
      onChange(ev) {
        interactivePreferences.update(ev.target.value, ev.target.checked);
      },
    }));
    // .concat({
    //   id: 'toggleAll',
    //   label: translateFunc(translateContext.interactiveToggleAll),
    //   value: '*',
    //   checked: allOff,
    //   onChange(ev) {
    //     interactiveTypes.forEach(({ name }) => {
    //       interactivePreferences.update(name, !ev.target.checked);
    //     });
    //   },
    // });
  }

  /**
   * Я почти уверен что вот такая вот „особенность“ — ересь,
   * но в дизайне под разделение настроек интерактива
   * маляры сделали отдельный заголовок для интерактива
   * @return {string}
   */
  get title() {
    const translateContext = this.context;
    if (this.state.currentSubmenu === SUBMENU_TYPE.INTERACTIVE) {
      return translateContext.interactive;
    }
    return translateContext.settings;
  }

  render() {
    // TODO need add after enable logic in CMS
    // const { qualities } = this.props;
    const {
      buttonsContainerHeight,
      // TODO need add after enable logic in CMS
      // currentSubmenu
    } = this.state;
    const translateContext = this.context;
    const { translateFunc } = translateContext;

    return (
      <MenuSettingsStyled>
        <TitleWrapper>
          <Title
            fixedSize
            color="gray_cod"
            size="fs_xs"
            font="inherit"
            weight="700"
          >
            {translateFunc(this.title)}
          </Title>
        </TitleWrapper>
        <ItemsWrapper height={buttonsContainerHeight}>
          <div ref={this.buttonsContainerRef}>
            {/*TODO need add after enable logic in CMS*/}
            {/*{currentSubmenu === SUBMENU_TYPE.QUALITIES &&*/}
            {/*renderBySubscriptionButton(*/}
            {/*qualities,*/}
            {/*translateFunc(translateContext.bitrateBySubscription),*/}
            {/*this.onBitrateBySubscriptionButtonClick*/}
            {/*)}*/}
            {this.content}
          </div>
        </ItemsWrapper>
        <BackButtonWrapper>
          <StyledButton
            isMobile
            hoverColor="green_turquoise"
            textColor="black"
            onClick={this.onClose}
            text={translateFunc(translateContext.closeButton)}
          />
        </BackButtonWrapper>
      </MenuSettingsStyled>
    );
  }
}

export default MenuSettings;
